import React, { ReactNode, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import conf from 'conf'
import { MaintenanceScreen, toMaintenanceLang } from 'services/maintenance'

import BlackoutPage from './BlackoutPage'
import ForceUpdateModal from './ForceUpdateModal'
import NoticeModal from './NoticeModal'

interface GuardState {
  maintenance: { screen: MaintenanceScreen }
  user: { authorized: boolean }
  settings?: { general?: { locale?: string } }
}

interface MaintenanceGuardProps {
  children: ReactNode
}

/**
 * 공지/점검 가드. Router·i18n·auth 보다 먼저 평가되도록 Provider 하위·Localization 상위에 둔다.
 * - page reload(mount) + 로그인 성공(authorized false→true) 시 notice.json 재평가.
 * - blackout: children 미렌더(풀페이지 차단). notice/forceUpdate: children + confirmBase 모달 오버레이.
 */
function MaintenanceGuard({ children }: MaintenanceGuardProps): React.ReactElement {
  const dispatch = useDispatch()
  const screen = useSelector((state: GuardState) => state.maintenance.screen)
  const authorized = useSelector((state: GuardState) => state.user.authorized)
  const locale = useSelector((state: GuardState) => state.settings?.general?.locale)
  const lang = toMaintenanceLang(locale)
  const prevAuthorizedRef = useRef(authorized)

  const fetchNotice = (): void => {
    dispatch({ type: 'maintenance/FETCH', payload: { url: conf.SERVER.maintenanceURL } })
  }

  // page reload(최초 진입/새로고침)
  useEffect(() => {
    fetchNotice()
  }, [])

  // 로그인 성공(false → true) 시 재평가
  useEffect(() => {
    if (!prevAuthorizedRef.current && authorized) {
      fetchNotice()
    }
    prevAuthorizedRef.current = authorized
  }, [authorized])

  if (screen.kind === 'blackout') {
    return <BlackoutPage url={screen.url} end={screen.end} lang={lang} />
  }
  if (screen.kind === 'forceUpdate') {
    return (
      <>
        {children}
        <ForceUpdateModal url={screen.url} lang={lang} />
      </>
    )
  }
  if (screen.kind === 'notice') {
    return (
      <>
        {children}
        <NoticeModal url={screen.url} modifiedAt={screen.modifiedAt} lang={lang} />
      </>
    )
  }

  return <>{children}</>
}

export default MaintenanceGuard
